import { Trans, useLingui } from "@lingui/react/macro";
import { BOT_INSTRUCTIONS_MAX_LENGTH } from "@rakazo/contracts";
import { Textarea } from "@rakazo/ui-web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useSpaceQueries } from "../lib/query";
import { useSpaceRpc } from "../lib/space-rpc";
import { errorText } from "../lib/user-error";

export function CompanyBotRole({ botId }: { botId: string }) {
  const queries = useSpaceQueries();
  const role = useQuery(queries.bots.identity.queryOptions({ input: { botId } }));
  return role.data ? (
    <RoleEditor
      key={role.data.id}
      botId={botId}
      value={role.data.sharedInstructions}
      canManage={role.data.canManage}
    />
  ) : null;
}

function RoleEditor({
  botId,
  value,
  canManage,
}: {
  botId: string;
  value: string;
  canManage: boolean;
}) {
  const id = useId();
  const { t } = useLingui();
  const rpc = useSpaceRpc();
  const queries = useSpaceQueries();
  const client = useQueryClient();
  const [draft, setDraft] = useState(value);
  const [baseline, setBaseline] = useState(value);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  if (value !== baseline && draft === baseline) {
    setBaseline(value);
    setDraft(value);
  }
  async function save() {
    if (pending || draft === value || !canManage) return;
    setPending(true);
    setError("");
    try {
      await rpc.bots.update({ botId, sharedInstructions: draft });
      await client.invalidateQueries({ queryKey: queries.bots.identity.key() });
      setBaseline(draft);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setPending(false);
    }
  }
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        <Trans>Company role</Trans>
      </label>
      <Textarea
        id={id}
        aria-label={t`Company role`}
        value={draft}
        readOnly={!canManage}
        disabled={pending}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void save()}
        maxLength={BOT_INSTRUCTIONS_MAX_LENGTH}
        className="min-h-20 field-sizing-content"
      />
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
